import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import logo from "../assets/cogniflock_logo_full.svg";
import { getApiBaseUrl } from "../utils/apiConfig";

const ConnectSlack = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [integrationStatus, setIntegrationStatus] = useState({ connected: false });
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = location.state || {};

  useEffect(() => {
    // Check if there's a code parameter in the URL (for OAuth callback)
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state === 'slack') {
      handleSlackCallback(code);
      // Remove code from URL to prevent multiple attempts
      navigate('/onboarding/connect-slack', { replace: true, state: { organizationId } });
    }
  }, [location, organizationId, navigate]);

  const handleSlackCallback = async (code) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      
      await axios.post(`${apiUrl}/integrations/slack/callback`,
        { 
          code, 
          organizationId: organizationId
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // After successful connection, navigate to dashboard
      navigate("/dashboard");
    } catch (error) {
      console.error("Error handling Slack callback:", error);
      setError("Failed to connect Slack. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const initiateSlackOAuth = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      const response = await axios.get(`${apiUrl}/integrations/slack/authorize`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Redirect to Slack authorization page
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error("Error initiating Slack OAuth:", error);
      setError("Failed to initiate Slack connection. Please try again.");
      setLoading(false);
    }
  };



  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-gradient-dash">
      <div className="w-full max-w-md p-6 flex flex-col gap-10">
        <img src={logo} alt="Logo" className="h-6" />
        
        <div className="text-center">
          <h2 className="text-xl font-normal text-black-0 mb-4">
            Connect Slack with Cogniflock
          </h2>
          <p className="text-grey-60 text-sm font-normal mb-4">
            Connect your Slack workspace to unlock powerful insights about your team's communication patterns, engagement levels, and collaboration dynamics.
          </p>
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-brand rounded-full"></div>
            <div className="w-2 h-2 bg-brand rounded-full"></div>
            <div className="w-2 h-2 bg-grey-30 rounded-full"></div>
          </div>
          <p className="text-xs text-grey-60">Step 2 of 3: Connect Slack</p>
        </div>

        <div className="flex flex-col items-center gap-6">
          <button
            onClick={initiateSlackOAuth}
            disabled={loading}
            className="w-full py-3 px-4 bg-brand text-white-f text-sm font-medium tracking-wide rounded-md hover:bg-brand-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="inline-block align-middle w-4 h-4 border-2 border-t-2 border-t-white-f border-bg-opacity rounded-full animate-spin"></div>
                <span>Connecting...</span>
              </div>
            ) : (
              "Connect Slack"
            )}
          </button>

          {error && (
            <p className="text-red-strong text-sm font-normal text-center">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectSlack; 